import { useState } from "react";

import { Button, Grid, Paper, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  activationStatDoctor,
  blockDocByUserCode,
  getAllDoctors,
  resetUserRetryCount,
} from "../../Api/Doctor/Doctor.Api";
import { ReactComponent as Person } from "../../Assets/Images/Doctor2.svg";
import { ReactComponent as Settings } from "../../Assets/Images/Setting.svg";
import SearchInput from "../../Components/atoms/Input/SearchInput";
import {
  Heading1,
  Heading3,
} from "../../Components/atoms/Typography/Heading/Heading";
import { AddDoctor } from "../../Components/organisms/AddDoctor/AddDoctor";
import { BasicGrid } from "../../Components/organisms/BasicGrid/BasicGrid";
import ConfirmDialog from "../../Components/organisms/ConfirmDialog/ConfirmDialog";
import { DoctorsConsultationSettings } from "../../Components/organisms/DoctorsConsultationSettings/DoctorsConsultationSettings";
import { ViewDoctorDetails } from "../../Components/organisms/ViewDoctorDetails/ViewDoctorDetails";
import { permissionValidator, useAuth } from "../../Store/AuthContext";
import { Permission } from "../../Utils/Constant/Permission";
import useApiData from "../../Utils/Hooks/useApiData";
import useLoadingState from "../../Utils/Hooks/useLodingState";
import useOverlayState from "../../Utils/Hooks/useOverlayState";
import { getColumns } from "./GridColumns";
import { AddDoctorAssistant } from "../../Components/organisms/AddDoctorAssistant/AddDoctorAssistant";
import { activationStatDoctorAssistant, getAllDoctorsAssistant,resendResetPasswordLink } from "../../Api/DoctorAssistant/DoctorAssistant.Api";

export const DoctorAssistantsGrid = () => {
  const { OverlayState, openOverlay, closeOverlay } = useOverlayState();
  const { LoadingState, startLoading, stopLoading } = useLoadingState();
  const { state } = useAuth();
  const [searchText, setSearchText] = useState("");

  const { t } = useTranslation();
  const [pageData, setPageData] = useState({
    currentPage: 1,
    pageCount: 0,
    pageSize: 10,
    totalPages: 0,
    totalRecordsCount: 0,
  });

  const apiCall = async () => {
    let langCode = new String(state?.lang?.code);
    const response = await getAllDoctorsAssistant({
      page: pageData.currentPage,
      size: pageData.pageSize,
      searchText: searchText,
      language: langCode.toUpperCase(),
    });
    if (response.type == 0) {
      return response;
    } else {
      throw new Error(response.message || "Failed to fetch doctor data");
    }
  };

  const { isLoading, data, error, refetch } = useApiData({
    apiCall: apiCall,
    dependency: [pageData.currentPage, pageData.pageSize, searchText],
    pagination: {
      isServerSidePagination: true,
      setApiData: (totalRecordsCount, pageSize, pageNumber, pagesCount) => {
        if (
          pageData.currentPage !== pageNumber ||
          pageSize !== pageData.pageSize
        ) {
          setPageData({
            ...pageData,
            currentPage:
              pageData.currentPage !== pageNumber
                ? pageNumber
                : pageData.currentPage,
            pageSize:
              pageSize !== pageData.pageSize ? pageSize : pageData.pageSize,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        } else {
          setPageData({
            ...pageData,
            totalPages: pagesCount,
            totalRecordsCount: totalRecordsCount,
          });
        }
      },
    },
  });

  const updateAccountStatus = async (record) => {
    OverlayState.visible && closeOverlay();
    startLoading({ btnName: "account-status-update" });

    await activationStatDoctor({
      id: record?.id,
      activeStatus: !record?.isActive,
    }).then((response) => {
      openOverlay({
        mode: Number(response.type),
        messages: {
          title: record?.isActive
            ? t("glossary:user.action.inactivate-account")
            : t("glossary:user.action.activate-account"),
          description:
            response.type == 0 && record?.isActive == true
              ? t("glossary:user.action.inactivate-account-desc")
              : response.type != 0 && record?.isActive == true
              ? t("glossary:user.action.inactivate-account-desc-error")
              : response.type == 0 && record?.isActive == false
              ? t("glossary:user.action.activate-account-desc")
              : t("glossary:user.action.activate-account-desc-error"),
        },
      });
      response.type == 0 && refetch();
    });
    stopLoading();
  };

  const blockDoc = async (record) => {
    closeOverlay();
    startLoading({ btnName: "block" });
    await blockDocByUserCode({
      docId: record?.docId,
      status: !record?.isBlocked,
      id: record?.id,
    }).then((response) => {
      openOverlay({
        mode: Number(response.type),
        messages: {
          title: record?.isBlocked
            ? t("glossary:doctor.un-block.title")
            : t("glossary:doctor.block.title"),
          description:
            record?.isBlocked && response.type == 0
              ? t("glossary:doctor.un-block.success")
              : record?.isBlocked && response.type != 0
              ? t("glossary:doctor.un-block.error")
              : !record?.isBlocked && response.type == 0
              ? t("glossary:doctor.block.success")
              : t("glossary:doctor.block.error"),
        },
        record: record,
      });
      response.type == 0 && refetch();
    });
    stopLoading();
  };

const clickHandler = async (e, data) => {

    // e represent index of action menu options
    if (e != 1) startLoading({ btnName: "load" });
    if (e === 1) {
    openOverlay({
      mode: "update",
      OverlayName: "new-doctor-assistant",
      record: data,
    });
    } else if (e === 2) {
      await resendResetPasswordLink({
              userId: data?.userId,
            }).then((response) => {
              openOverlay({
                messages: {
                  title:
                    response.type == 0
                      ? t("glossary:user.action.reset-password")
                      : t("glossary:user.action.reset-password"),
                  description:
                    response.type == 0
                      ? t("glossary:user.action.reset-password-desc")
                      : t("validation:password-link-send-fail"),
                },
              });
            });
    } else if (e === 3) {
      await activationStatDoctorAssistant({
        id: data?.id,
        activeStatus: !data?.isActive,
      }).then((response) => {
        openOverlay({
          mode: Number(response.type),
          messages: {
            title: data?.isActive
              ? t("glossary:user.action.inactivate-account")
              : t("glossary:user.action.activate-account"),
            description:
              response.type == 0 && data?.isActive == true
                ? t("glossary:user.action.inactivate-account-desc")
                : response.type != 0 && data?.isActive == true
                ? t("glossary:user.action.inactivate-account-desc-error")
                : response.type == 0 && data?.isActive == false
                ? t("glossary:user.action.activate-account-desc")
                : t("glossary:user.action.activate-account-desc-error"),
          },
        });
        response.type == 0 && refetch();
      });
    } else if (e === 4) {
      await activationStatDoctorAssistant({
        id: data?.id,
        activeStatus: !data?.isActive,
      }).then((response) => {
        openOverlay({
          mode: Number(response.type),
          messages: {
            title: data?.isActive
              ? t("glossary:user.action.inactivate-account")
              : t("glossary:user.action.activate-account"),
            description:
              response.type == 0 && data?.isActive == true
                ? t("glossary:user.action.inactivate-account-desc")
                : response.type != 0 && data?.isActive == true
                ? t("glossary:user.action.inactivate-account-desc-error")
                : response.type == 0 && data?.isActive == false
                ? t("glossary:user.action.activate-account-desc")
                : t("glossary:user.action.activate-account-desc-error"),
          },
        });
        response.type == 0 && refetch();
      });
    } else if (e === 5) {
      await resetUserRetryCount(data?.email).then((response) => {
        openOverlay({
          mode: Number(response.type),
          messages: {
            title: t("glossary:auth.reset-otp-count.reset-otp-count"),
            description:
              response.type == 0
                ? t("glossary:auth.reset-otp-count.succeed-message")
                : t("glossary:auth.reset-otp-count.unsucceed-message"),
          },
        });
        response.type == 0 && refetch();
      });
    }
    stopLoading();
  };

  return (
    <>
      <Grid container direction={"column"}>
        <Grid item>
          <Stack direction={"row"} justifyContent={"space-between"} mb={2.8}>
            <Heading1 text={t("glossary:doctor-assistant.title")} />
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <SearchInput
                width={"319.42px"}
                searchValue={searchText}
                placeholder={t("common:placeholder.doctor-assistant-search")}
                getSearchText={(value) => {
                  setSearchText(value);
                  setPageData({ ...pageData, currentPage: 1 });
                }}
              />

              <Button
                disableTouchRipple
                variant="lightBlue"
                sx={{ borderRadius: "14px" }}
                onClick={() => {
                  openOverlay({ OverlayName: "new-doctor-assistant" });
                }}
              >
                <Person style={{ marginRight: "10px" }} fill="#ffffff" />
                <Heading3
                  text={t("glossary:add-doctor-assistant.newdoctorassistant")}
                  color="#ffffff"
                />
              </Button>

            </Stack>
          </Stack>
        </Grid>
        <Grid width="100%" item>
          <Paper
            sx={{
              padding: "20px 24px",
              boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.05)",
              position: "relative",
              height: "100%",
            }}
          >
            <BasicGrid
              // isError={error}
              isLoading={isLoading || LoadingState.visible}
              rowSelect={false}
              data={data}
              pageData={pageData}
              columnArray={getColumns(t, clickHandler)}
              allowSorting
              allowPaging
              field={["EmployeeID", "LastName"]}
              onRowClick={(rowData) => {
                openOverlay({
                  OverlayName: "view-doctor",
                  record: rowData,
                  mode: "view",
                });
              }}
              paginationClick={(pageNumber) => {
                setPageData({ ...pageData, currentPage: pageNumber });
              }}
              pageSizeChange={(selectedPageSize) => {
                setPageData({ ...pageData, currentPage: 1, pageSize: selectedPageSize });
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {OverlayState.visible && OverlayState.OverlayName === "new-doctor-assistant" && (
        <AddDoctorAssistant
          close={closeOverlay}
          OpenDrawer={OverlayState.visible}
          mode={OverlayState.mode}
          data={OverlayState.record}
          loadDoctorAssitants={refetch}
        />
      )}
      {OverlayState.visible && OverlayState.OverlayName === "view-doctor" && (
        <ViewDoctorDetails
          data={OverlayState.record}
          close={closeOverlay}
          OpenDrawer={OverlayState.visible}
          loadDoctors={refetch}
        />
      )}
      {OverlayState.visible && OverlayState.OverlayName === "settings" && (
        <DoctorsConsultationSettings
          data={OverlayState.record}
          close={closeOverlay}
          OpenDrawer={OverlayState.visible}
        />
      )}

      {OverlayState.visible &&
        !OverlayState.OverlayName &&
        OverlayState?.messages?.title && (
          <ConfirmDialog
            isError={OverlayState.mode === "fail" || OverlayState.mode === 3}
            title={OverlayState.messages?.title}
            description={OverlayState.messages?.description}
            open={OverlayState?.visible}
            close={() => {
              closeOverlay();
            }}
            onOkClick={() => {
              closeOverlay();
            }}
          />
        )}
      {OverlayState.visible &&
        OverlayState.OverlayName &&
        OverlayState?.messages?.title && (
          <ConfirmDialog
            title={OverlayState.messages?.title}
            description={OverlayState.messages?.description}
            open={OverlayState?.visible}
            close={closeOverlay}
            onNoClick={closeOverlay}
            onOkTitle={t("common:alert-confirm.yes")}
            onOkClick={async () => {
              if (OverlayState.OverlayName === "activate-not-verified") {
                updateAccountStatus(OverlayState.record);
              } else if (OverlayState.OverlayName === "block") {
                blockDoc(OverlayState.record);
              }
            }}
          />
        )}
    </>
  );
};
